import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography
} from '@mui/material';
import {
    convertSkillToAnalyticsId,
    convertSkillToTitleCase
} from '../../functions/dataFormatting';
import { VictoryAxis, VictoryBar, VictoryChart } from 'victory';
import React from 'react';
import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

const hiddenOnSmallScreens = {
    display: {
        xs: 'table-cell',
        sm: 'none',
        md: 'table-cell'
    }
};

const tableCellPadding = {
    paddingTop: 11,
    paddingBottom: 11,
    paddingLeft: 8,
    paddingRight: 8
};

const StyledTableCell = styled(TableCell)(() => ({
    ...tableCellPadding
}));

const StyledTableHeaderCell = styled(TableCell)(({ theme }) => ({
    ...tableCellPadding,
    borderColor: theme.palette.rankingTable.border
}));

/** @component
 * Represents the ba score breakdown by skill table.
 *
 * This is largely a hack to hit the design criteria. They want a graph inside a table.
 * Since this isn't directly supported by our third party library, this
 * component indirectly solves it by producing a list of very small rotated & then vertically aligned bar charts
 * with a single data value each, to be placed in each cell of the column
 * that's being overlaid with a "graph".
 * @param data
 * @returns {Element}
 * @constructor
 */
const SkillsTable = ({ data }) => {
    const isSmallScreenSize = useMediaQuery((theme) =>
        theme.breakpoints.between('sm', 'md')
    );

    return (
        <Table
            stickyHeader
            sx={{ tableLayout: 'fixed' }}
            aria-label='skillsTable'
            id='skillsTable'
            data-analyticsid='portfolio-drilldown-skill-table'
        >
            <colgroup>
                <col width='30%' />
                {!isSmallScreenSize && <col width='50%' />}
                <col width='20%' />
            </colgroup>
            <TableHead>
                <TableRow>
                    <StyledTableHeaderCell sx={{ paddingLeft: 2 }}>
                        Skill
                    </StyledTableHeaderCell>
                    <StyledTableHeaderCell sx={hiddenOnSmallScreens} />
                    <StyledTableHeaderCell
                        align='center'
                        sx={{ paddingLeft: 0, paddingRight: 0 }}
                    >
                        Skill Score
                    </StyledTableHeaderCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {data?.map((row) => (
                    <TableRow
                        key={row.skill}
                        data-analyticsid={convertSkillToAnalyticsId(row.skill)}
                    >
                        <StyledTableCell
                            sx={{ paddingLeft: 2, paddingRight: 0 }}
                        >
                            <Typography variant='body2'>
                                {convertSkillToTitleCase(row.skill)}
                            </Typography>
                        </StyledTableCell>
                        <StyledTableCell
                            sx={{
                                ...hiddenOnSmallScreens,
                                position: 'relative',
                                width: '100%',
                                height: '100%',
                                paddingTop: 1,
                                paddingBottom: 1
                            }}
                        >
                            <VictoryChart
                                padding={{
                                    top: 20,
                                    bottom: 24
                                }}
                                domainPadding={{ x: 15 }}
                                width={400}
                                height={60}
                            >
                                <VictoryAxis
                                    axisValue={50}
                                    height={400}
                                    style={{
                                        axis: { opacity: 0 }
                                    }}
                                    tickValues={[]}
                                />
                                <VictoryBar
                                    horizontal
                                    domain={{
                                        x: [0.9, 1.9],
                                        y: [0, 100]
                                    }}
                                    data={[row]}
                                    x={1}
                                    y0={(datum) =>
                                        datum.baScore > 50 ? datum.baScore : 50
                                    }
                                    y={(datum) =>
                                        datum.baScore > 50 ? 50 : datum.baScore
                                    }
                                    barWidth={60}
                                    style={{
                                        data: {
                                            fill: ({ datum }) => datum.barColor
                                        }
                                    }}
                                />
                            </VictoryChart>
                            <Box
                                style={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    width: '0.5px',
                                    height: '100%',
                                    backgroundColor: '#e0e0e0',
                                    transform: 'translate(-50%, -50%)'
                                }}
                            />
                        </StyledTableCell>
                        <StyledTableCell align='center' sx={{ width: '35%' }}>
                            {row.baScore?.toFixed(1)}
                        </StyledTableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
};

SkillsTable.propTypes = {
    data: PropTypes.arrayOf(PropTypes.object)
};

export default SkillsTable;
